/**
 * Builds CES-readable page context for get_page_information:
 * plan(s), perk(s), accessory / accessories, express-checkout cart.
 */

import {
  buildExpressCheckoutCartPageInformationText,
  getCartDetailsJsonObject,
} from './planPageInformationExpressCheckout';

export {
  buildExpressCheckoutCartPageInformationText,
  getCartDetailsJsonObject,
  mergeCartContextForExpressCheckout,
} from './planPageInformationExpressCheckout';

const FWA_PLANS_PAGE_LABEL = 'FWA Plans Page';
const FWA_ADDONS_PAGE_LABEL = 'FWA Addons';
const FWA_STREAMLINE_PAGE_LABEL = 'FWA Streamline Ordering';
const FWA_EXPRESS_CHECKOUT_PAGE_LABEL = 'FWA Express Checkout';

/**
 * FWA plan highlights appended to get_page_information (plan / plans) for CES.
 */
const FWA_CES_PLAN_INFO_LINES = [
  'Up to 300 Mbps download speeds',
  'Typical speeds of 85 - 250 Mbps',
  'Unlimited premium data',
  'Ultra HD 4K video streaming',
  '5-year price lock guarantee',
  'Router + Whole-Home Wi-Fi included',
  '1 Wi-Fi Extender for more reliable coverage',
  'Free router upgrade after 3 years ($50 value)',
  'Free professional setup',
  'For a limited time, on us ($99 value)',
  'Easy self setup, always available',
];

function formatPlanInfoNodeForCes() {
  const body = FWA_CES_PLAN_INFO_LINES.map((line) => `- ${line}`).join('\n');
  return `[PLAN INFO]\n${body}`;
}

export function extractGetPageInformationCategory(message) {
  const raw =
    message?.data?.fields?.category?.stringValue ??
    message?.data?.category ??
    message?.data?.fields?.category ??
    '';
  return String(raw).toLowerCase().trim();
}

function getFirstLine(progressivePlansState) {
  const lineLevel = progressivePlansState?.progressivePlanAPiResponse?.data?.lineLevelPlans;
  if (!lineLevel?.lines?.length) return null;
  return lineLevel.lines[0];
}

function getDisplayedPlans(progressivePlansState) {
  const products = getFirstLine(progressivePlansState)?.products;
  if (!products?.length) return [];
  return products[0]?.plans || [];
}

function formatPriceSummary(price) {
  if (!price || typeof price !== 'object') return null;
  const parts = [];
  if (price.discountedPrice != null) parts.push(`discounted monthly: $${price.discountedPrice}`);
  if (price.regularPrice != null) parts.push(`regular: $${price.regularPrice}`);
  if (price.regPriceWOAutoPay != null) parts.push(`without autopay: $${price.regPriceWOAutoPay}`);
  return parts.length ? parts.join('; ') : null;
}

/** Matches GetSpecialOffersSection / plan promotions — human-readable for CES. */
const CES_DETAILS_TAB_CTA = 'Click on the Details tab to get more information.';

const CES_OFFER_SKIP_PLACEMENTS = new Set([
  'TILE-FOOTER',
  'ACCORD-FOOTER',
  'CAP',
  'ACCORD-LABEL',
  'INCLUDED-HEADER-TEXT',
  'HEADER-IMAGE',
  'ACCORD-HEADER-SS',
]);

function findPromoBadgeText(promo, placement) {
  const msg = promo?.badgeMessages?.find((m) => m?.placement === placement);
  const t = msg?.badgeText;
  return t != null && String(t).trim() !== '' ? String(t).trim() : '';
}

function shouldIncludePromoInCesOffers(promo) {
  if (promo?.choiceOffer) return true;
  const msgs = promo?.badgeMessages;
  if (!Array.isArray(msgs) || msgs.length === 0) {
    return false;
  }
  const isNumericOnly = (txt) => /^\d+$/.test(String(txt || '').trim());
  return msgs.some((m) => {
    const t = String(m.badgeText || '').trim();
    return !CES_OFFER_SKIP_PLACEMENTS.has(m.placement) && t !== '' && !isNumericOnly(t);
  });
}

/**
 * @returns {string[]} Ordered lines for one promotion (title, value, details CTA).
 */
export function buildCesOfferLinesForPromo(plan, promo) {
  const planName = plan?.displayName || '';
  const replacePlan = (s) => String(s).replace(/\[xx\]/g, planName);

  const itemHeader = replacePlan(findPromoBadgeText(promo, 'ITEM-HEADER'));
  const itemTitle = findPromoBadgeText(promo, 'ITEM-TITLE');
  const composedHeadline = [itemHeader, itemTitle].filter(Boolean).join(' ').trim();

  const lines = [];
  const seen = new Set();

  const pushUnique = (line) => {
    const t = String(line).trim();
    if (!t || seen.has(t)) return;
    seen.add(t);
    lines.push(t);
  };

  const eyebrow = findPromoBadgeText(promo, 'PERK-EYEBROW');
  const displayName = (promo?.displayName || '').trim();

  if (composedHeadline) {
    pushUnique(composedHeadline);
  } else if (eyebrow) {
    pushUnique(eyebrow);
  } else if (displayName) {
    pushUnique(displayName);
  }

  const badgeHeader = findPromoBadgeText(promo, 'BADGE-HEADER');
  if (badgeHeader) pushUnique(badgeHeader);

  const accordHeader = findPromoBadgeText(promo, 'ACCORD-HEADER');
  if (accordHeader && !badgeHeader) pushUnique(accordHeader);

  const itemBody = promo?.badgeMessages?.find((m) => m?.placement === 'ITEM-BODY' && m?.badgeText)?.badgeText;
  if (itemBody) {
    itemBody.split('|').forEach((part) => pushUnique(part.trim()));
  }

  const subHeader = findPromoBadgeText(promo, 'BADGE-SUB-HEADER');
  if (subHeader) pushUnique(subHeader);

  const perkItemBody = findPromoBadgeText(promo, 'PERK-ITEM-BODY');
  if (perkItemBody) pushUnique(perkItemBody);

  const hasDetails =
    promo?.badgeMessages?.some((m) => {
      return (
        (m?.placement === 'BADGE-SUB-HEADER-LINK' && Boolean(m?.badgeText?.trim())) ||
        (typeof m?.badgeToolTip === 'string' && Boolean(m.badgeToolTip.trim())) ||
        m?.overlayContent === true
      );
    }) ?? false;

  if (hasDetails) {
    const linkLabel = findPromoBadgeText(promo, 'BADGE-SUB-HEADER-LINK') || 'Details';
    pushUnique(`${linkLabel}. ${CES_DETAILS_TAB_CTA}`);
  }

  return lines;
}

function formatPlanOffersBlock(plan) {
  const promos = (plan?.promotions || []).filter(shouldIncludePromoInCesOffers);
  if (!promos.length) return '';

  const blocks = promos
    .map((promo, idx) => {
      const offerLines = buildCesOfferLinesForPromo(plan, promo);
      if (!offerLines.length) return null;
      const inner = offerLines.map((l) => `  - ${l}`).join('\n');
      return `[OFFER ${idx + 1}]\n${inner}`;
    })
    .filter(Boolean);

  if (!blocks.length) return '';
  return `- offers (${blocks.length}):\n${blocks.join('\n')}`;
}

function formatPlanBlock(plan, index) {
  const lines = [`[# PLAN ${index + 1}]`];
  if (plan?.displayName) lines.push(`- displayName: ${plan.displayName}`);
  if (plan?.planId != null) lines.push(`- planId: ${plan.planId}`);
  const priceStr = formatPriceSummary(plan?.price);
  if (priceStr) lines.push(`- pricing: ${priceStr}`);
  if (plan?.hasEligibleAutoPayDiscount != null) {
    lines.push(`- autoPayDiscountEligible: ${plan.hasEligibleAutoPayDiscount}`);
  }
  const offersBlock = formatPlanOffersBlock(plan);
  if (offersBlock) lines.push(offersBlock);
  return lines.join('\n');
}

function getSelectedPlan(progressivePlansState) {
  const raw = progressivePlansState?.selectedPlanInfo?.selectedPlan;
  if (!raw) return null;
  if (Array.isArray(raw)) return raw.length === 1 ? raw[0] : null;
  return raw;
}

function normalizePerkReferenceMap(perkReferenceDataList) {
  if (!perkReferenceDataList) return {};
  if (Array.isArray(perkReferenceDataList)) {
    const map = {};
    perkReferenceDataList.forEach((item) => {
      if (item?.spoId != null) map[String(item.spoId)] = item;
    });
    return map;
  }
  return perkReferenceDataList;
}

function getDisplayableAvailablePerks(progressivePlansState) {
  const line = getFirstLine(progressivePlansState);
  const raw = line?.availablePerks || [];
  return raw.filter((perk) => !perk?.skipForDisplay);
}

function formatPerkBlock(perk, refMeta, index) {
  const spoId = perk?.spoId != null ? String(perk.spoId) : '';
  const lines = [`[# PERK ${index + 1}]`];
  if (spoId) lines.push(`- spoId: ${spoId}`);
  const title = refMeta?.perkTitle || perk?.perkTitle || perk?.displayName || perk?.name;
  if (title) lines.push(`- title: ${title}`);
  if (refMeta?.perkInclusion) lines.push(`- summary: ${refMeta.perkInclusion}`);
  if (refMeta?.perkOriginalPrice != null) lines.push(`- originalValue: $${refMeta.perkOriginalPrice}`);
  const price = perk?.price;
  if (price && typeof price === 'object') {
    const p = formatPriceSummary(price);
    if (p) lines.push(`- pricing: ${p}`);
    if (price.totalAmountOff != null) lines.push(`- totalAmountOff: $${price.totalAmountOff}`);
  }
  return lines.join('\n');
}

/**
 * Available perks on the plan / streamline flow plus current selections.
 */
export function buildPerkPageInformationText(progressivePlansState, pageLabel = FWA_PLANS_PAGE_LABEL) {
  const perkRef = normalizePerkReferenceMap(
    progressivePlansState?.progressivePlanAPiResponse?.data?.perkReferenceDataList
  );
  const available = getDisplayableAvailablePerks(progressivePlansState);
  const selectedPlan = getSelectedPlan(progressivePlansState);
  const selectedPerks = progressivePlansState?.selectedPlanInfo?.selectedPerks || [];

  if (!available.length) {
    return 'No perks are available';
  }

  const parts = [`page_name: ${pageLabel}`];
  const selectedPlanName = selectedPlan?.displayName || selectedPlan?.name;
  if (selectedPlanName) parts.push(`[CUSTOMER PLAN CONTEXT: ${selectedPlanName}]`);

  available.forEach((perk, i) => {
    const meta = perk?.spoId != null ? perkRef[String(perk.spoId)] : null;
    parts.push(formatPerkBlock(perk, meta, i));
  });

  const selectedIds = new Set(selectedPerks.map((p) => (p?.spoId != null ? String(p.spoId) : '')).filter(Boolean));
  if (selectedIds.size) {
    parts.push(`[SELECTED PERK SPO IDs: ${[...selectedIds].join(', ')}]`);
  }
  if (selectedPerks.length) {
    const names = selectedPerks
      .map((p) => p?.perkTitle || p?.name || perkRef[String(p?.spoId)]?.perkTitle || p?.spoId)
      .filter(Boolean);
    if (names.length) parts.push(`[SELECTED PERK NAMES: ${names.join(', ')}]`);
  }

  return parts.join('\n\n');
}

function formatAccessoryBlock(item, index) {
  const lines = [`[# ACCESSORY ${index + 1}]`];
  const sku = item?.skuDetails || {};
  if (sku.sorId) lines.push(`- sorId: ${sku.sorId}`);
  if (sku.productId) lines.push(`- productId: ${sku.productId}`);
  if (item?.type) lines.push(`- type: ${item.type}`);
  const name =
    sku.productName || sku.displayName || item.displayName || item.name || item.title || item.productName;
  if (name) lines.push(`- name: ${name}`);
  if (sku.monthlyPrice != null) lines.push(`- monthlyPrice: ${sku.monthlyPrice}`);
  if (sku.dueMonthly != null) lines.push(`- dueMonthly: ${sku.dueMonthly}`);
  if (sku.dueToday != null) lines.push(`- dueToday: ${sku.dueToday}`);
  return lines.join('\n');
}

function cartAccessorySummary(cartDetails) {
  const dueToday = getCartDetailsJsonObject(cartDetails)?.dueTodayList ?? cartDetails?.cartDetailsJson?.dueTodayList;
  if (!Array.isArray(dueToday) || !dueToday.length) return null;
  const bits = dueToday
    .map((row) => {
      const id = row?.sorId || row?.productId;
      const qty = row?.quantity;
      const nm = row?.productName || row?.name;
      if (!id && !nm) return null;
      /* istanbul ignore next -- exercised via integration; branch matrix is combinatorial */
      return qty != null && id ? `${nm || id} x${qty}` : nm || String(id);
    })
    .filter(Boolean);
  return bits.length ? `[ACCESSORIES IN CART: ${bits.join('; ')}]` : null;
}

/**
 * Add-ons page streaming devices / accessories from Redux addons slice.
 */
export function buildAccessoryPageInformationText(addons, cartDetails, pageLabel = FWA_ADDONS_PAGE_LABEL) {
  const raw = addons?.accessoryDetails;
  const list = Array.isArray(raw) ? raw : [];
  if (!list.length) {
    return 'No accessories are available';
  }
  const parts = [`page_name: ${pageLabel}`];
  if (addons?.selectedProdID) {
    parts.push(`[SELECTED ACCESSORY REFERENCE ID: ${addons.selectedProdID}]`);
  }
  list.forEach((item, i) => parts.push(formatAccessoryBlock(item, i)));
  const cartLine = cartAccessorySummary(cartDetails);
  if (cartLine) parts.push(cartLine);
  return parts.join('\n\n');
}

/**
 * @param {object} progressivePlansState — Redux progressivePlans slice
 * @param {string} [pageLabel] — VoiceBot page label (e.g. FWA Plans Page)
 * @returns {string} Text tool result for CES
 */
export function buildPlanPageInformationText(progressivePlansState, pageLabel = FWA_PLANS_PAGE_LABEL) {
  const plans = getDisplayedPlans(progressivePlansState);
  const selectedPlan = getSelectedPlan(progressivePlansState);
  const selectedPerks = progressivePlansState?.selectedPlanInfo?.selectedPerks || [];

  if (!plans.length) {
    return 'No plans are available';
  }

  const parts = [`page_name: ${pageLabel}`];
  const selectedName = selectedPlan?.displayName || selectedPlan?.name;
  if (selectedName) parts.push(`[SELECTED PLAN: ${selectedName}]`);

  plans.forEach((plan, i) => parts.push(formatPlanBlock(plan, i)));

  if (selectedPerks.length) {
    const perkNames = selectedPerks
      .map((p) => p?.perkTitle || p?.name || p?.spoId)
      .filter(Boolean);
    if (perkNames.length) {
      parts.push(`[SELECTED PERKS: ${perkNames.join(', ')}]`);
    }
  }

  parts.push(formatPlanInfoNodeForCes());

  return parts.join('\n\n');
}

export const VOICE_BOT_FWA_PLANS_PAGE = FWA_PLANS_PAGE_LABEL;
export const VOICE_BOT_FWA_ADDONS_PAGE = FWA_ADDONS_PAGE_LABEL;
export const VOICE_BOT_FWA_STREAMLINE_PAGE = FWA_STREAMLINE_PAGE_LABEL;
export const VOICE_BOT_FWA_EXPRESS_CHECKOUT_PAGE = FWA_EXPRESS_CHECKOUT_PAGE_LABEL;

/** Plans shown on PDP (same source as CES plan context). */
export function getDisplayedPlansForVoiceBot(progressivePlansState) {
  return getDisplayedPlans(progressivePlansState);
}

/**
 * Match CES / voice plan label to a plan object for UPDATE_SELECT_DATA_PLAN.
 * @returns {object|null}
 */
export function findProgressivePlanByVoiceValue(progressivePlansState, rawValue) {
  if (rawValue == null || String(rawValue).trim() === '') return null;
  const needle = String(rawValue).trim().toLowerCase();
  const plans = getDisplayedPlans(progressivePlansState);
  return (
    plans.find((p) => {
      const d = p?.displayName != null ? String(p.displayName).toLowerCase() : '';
      const n = p?.name != null ? String(p.name).toLowerCase() : '';
      const id = p?.planId != null ? String(p.planId).toLowerCase() : '';
      return needle === d || needle === n || needle === id;
    }) || null
  );
}

/**
 * Match CES / voice perk label to an available perk + title for UPDATE_SELECT_DATA_PERK.
 * @returns {{ perk: object, perkTitle: string }|null}
 */
export function findAvailablePerkByVoiceValue(progressivePlansState, rawValue) {
  if (rawValue == null || String(rawValue).trim() === '') return null;
  const needle = String(rawValue).trim().toLowerCase();
  const perkRef = normalizePerkReferenceMap(
    progressivePlansState?.progressivePlanAPiResponse?.data?.perkReferenceDataList
  );
  const available = getDisplayableAvailablePerks(progressivePlansState);
  let match = available.find((perk) => {
    const meta = perk?.spoId != null ? perkRef[String(perk.spoId)] : null;
    const title = meta?.perkTitle || perk?.perkTitle || perk?.displayName || perk?.name || '';
    const t = title ? String(title).toLowerCase() : '';
    const n = perk?.name != null ? String(perk.name).toLowerCase() : '';
    const spo = perk?.spoId != null ? String(perk.spoId).toLowerCase() : '';
    return needle === t || needle === n || needle === spo;
  });
  if (!match) {
    match = available.find((perk) => {
      const meta = perk?.spoId != null ? perkRef[String(perk.spoId)] : null;
      const title = meta?.perkTitle || perk?.perkTitle || perk?.displayName || perk?.name || '';
      const t = title ? String(title).toLowerCase() : '';
      return t && (t.includes(needle) || needle.includes(t));
    });
  }
  if (!match) return null;
  const meta = match?.spoId != null ? perkRef[String(match.spoId)] : null;
  const candidates = [meta?.perkTitle, match?.perkTitle, match?.displayName, match?.name].filter(Boolean);
  const perkTitle =
    candidates.find((t) => String(t).trim().toLowerCase() === needle) ||
    meta?.perkTitle ||
    match?.perkTitle ||
    match?.displayName ||
    match?.name ||
    '';
  return { perk: match, perkTitle };
}

/**
 * Picks live context string for VoiceBot textMessage by current page label.
 */
export function buildVoiceBotPageContextText(pageLabel, progressivePlans, addons, cartDetails, checkoutState) {
  if (pageLabel === FWA_ADDONS_PAGE_LABEL) {
    return buildAccessoryPageInformationText(addons, cartDetails, pageLabel);
  }
  if (pageLabel === FWA_STREAMLINE_PAGE_LABEL) {
    return buildPerkPageInformationText(progressivePlans, pageLabel);
  }
  if (pageLabel === FWA_PLANS_PAGE_LABEL) {
    return buildPlanPageInformationText(progressivePlans, pageLabel);
  }
  if (pageLabel === FWA_EXPRESS_CHECKOUT_PAGE_LABEL) {
    return buildExpressCheckoutCartPageInformationText(cartDetails, checkoutState, pageLabel);
  }
  return '';
}
